import styled, { css, keyframes } from "styled-components";
import { borderRadius, spacing, typographyMixin } from "@magicbi/ui-library";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius as pxRadius } from "../../../design-system/tokens/borders";
import { CARD_HEIGHT, STACK_HEIGHT } from "./CardStack.constants";
import type { CardStackAccent } from "./CardStack.types";

const cycle = keyframes`
  0%, 28% { transform: translateY(0) scale(1); opacity: 1; z-index: 3; }
  31% { transform: translateY(18px) scale(.98); opacity: 0; z-index: 3; }
  32% { transform: translateY(-20px) scale(.88); opacity: 0; z-index: 1; }
  36%, 61% { transform: translateY(-20px) scale(.88); opacity: .55; z-index: 1; }
  67%, 94% { transform: translateY(-10px) scale(.94); opacity: .8; z-index: 2; }
  100% { transform: translateY(0) scale(1); opacity: 1; z-index: 3; }
`;

export const Stack = styled.div`
  position: relative;
  width: 100%;
  height: ${STACK_HEIGHT};
`;

/* GAP: blush end stop #F4EAF3 has no solid token (only inside backgroundGradient). */
const BLUSH = "#F4EAF3";

/** Named 135deg gradients of the design: capture (primary > lavender 55% > secondary), invite (secondary 700 > 300), connect (peach > blush). */
const accentBackground = (accent: CardStackAccent) => ({ theme }: { theme: any }) =>
  accent === "info"
    ? `linear-gradient(135deg, ${theme.colors.secondary?.[700] ?? rawColors.secondary[700]}, ${theme.colors.secondary?.[300] ?? rawColors.secondary[300]})`
    : accent === "warning"
      ? `linear-gradient(135deg, ${rawColors.accent.peach}, ${BLUSH})`
      : `linear-gradient(135deg, ${theme.colors.primary[500]}, ${rawColors.primary.lavender} 55%, ${theme.colors.secondary?.[500] ?? rawColors.secondary[500]})`;

export const Card = styled.button<{ $index: number; $animate: boolean; $disabled: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${CARD_HEIGHT};
  box-sizing: border-box;
  padding: 0 0 0 ${spacing[4]};
  display: flex;
  align-items: center;
  gap: ${spacing[2.5]};
  overflow: hidden;
  text-align: left;
  font: inherit;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  border: 1px solid ${rawColors.text.strongEmphasis};
  border-radius: ${borderRadius["2xl"]};
  background: ${rawColors.brand.primaryDarker};
  color: ${rawColors.text.white};
  transform-origin: 50% 0;
  z-index: ${({ $index }) => 3 - $index};
  ${({ $animate, $index }) =>
    $animate &&
    css`
      animation: ${cycle} 9s cubic-bezier(0.22, 1, 0.36, 1) infinite;
      animation-delay: -${[0, 6, 3][$index] ?? 0}s;
    `}

  ${({ $animate }) => $animate && css`
    ${Stack}:hover & { animation-play-state: paused; }
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary[500]};
    outline-offset: 2px;
  }
`;

export const Text = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[0.5]};
`;

export const TitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing[1.5]};
  ${typographyMixin("body", 4)}
  font-weight: 700;
  flex-wrap: wrap;
  white-space: normal;
  overflow-wrap: anywhere;
`;

export const Subtitle = styled.span`
  /* GAP: design 11px; no 11px type token. */
  font-size: 0.6875rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  white-space: normal;
  overflow-wrap: anywhere;
  color: ${rawColors.primary.lavender};
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  /* GAP: design 18px tall; no token. */
  height: 1.125rem;
  padding: 0 ${spacing[1.5]};
  border-radius: ${borderRadius.full};
  background: ${rawColors.brand.primaryDarker};
  color: ${rawColors.stroke[200]};
  /* GAP: design 10px/600; no 10px type token. */
  font-size: 0.625rem;
  font-weight: 600;
  white-space: nowrap;
`;

export const IconPanel = styled.span<{ $accent: CardStackAccent }>`
  flex: none;
  width: ${CARD_HEIGHT};
  height: ${CARD_HEIGHT};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${pxRadius[22]} 0 0 ${pxRadius[22]};
  background: ${({ $accent }) => accentBackground($accent)};
  color: ${({ theme }) => theme.colors.text.inverse};
`;
